import React, { useState, useEffect } from 'react';
import { Heart, Mail, Facebook, Instagram, Youtube, CheckCircle, Loader2 } from 'lucide-react';
import { mockContactInfo } from '../data/mock';
import { contactApi } from '../services/api';
import ChurchLogo from './ChurchLogo';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [logoVariant, setLogoVariant] = useState('yellow-blue');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMsg, setNewsletterMsg] = useState(null);

  useEffect(() => {
    const savedVariant = localStorage.getItem('icg-church-logo-variant');
    if (savedVariant) {
      setLogoVariant(savedVariant);
    }
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterLoading(true);
    setNewsletterMsg(null);
    try {
      const result = await contactApi.subscribeNewsletter(newsletterEmail);
      setNewsletterMsg({ type: 'success', text: result.message });
      setNewsletterEmail('');
    } catch {
      setNewsletterMsg({ type: 'error', text: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.' });
    } finally {
      setNewsletterLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* About Column */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-white/10 backdrop-blur-sm p-2 rounded-xl">
                <ChurchLogo variant={logoVariant} size="small" />
              </div>
              <div>
                <div className="font-bold text-lg">ICG Hannover</div>
                <div className="text-sm text-gray-400">Internationale Christengemeinde</div>
              </div>
            </div>
            <p className="text-gray-400 mb-4">
              Eine internationale Familie von Gläubigen, die gemeinsam Jesus nachfolgen und 
              Menschen aus allen Nationen willkommen heißen.
            </p>
            <div className="flex items-center gap-2 text-blue-300">
              <Heart className="w-5 h-5" />
              <span className="text-sm">Seit 2009</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-6">Schnelllinks</h3>
            <ul className="space-y-3">
              {[
                { id: 'home', label: 'Start' },
                { id: 'about', label: 'Über uns' },
                { id: 'livestream', label: 'Live-Stream' },
                { id: 'sermons', label: 'Predigten' },
                { id: 'events', label: 'Veranstaltungen' },
                { id: 'contact', label: 'Kontakt' }
              ].map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Times */}
          <div>
            <h3 className="font-bold text-lg mb-6">Gottesdienstzeiten</h3>
            <ul className="space-y-3 text-gray-400">
              <li>
                <div className="font-semibold text-white">Sonntag</div>
                <div className="text-sm">16:00 - 18:00 Uhr</div>
                <div className="text-sm">Hauptgottesdienst</div>
              </li>
              <li className="pt-2">
                <div className="font-semibold text-white">Mittwoch</div>
                <div className="text-sm">19:00 - 20:30 Uhr</div>
                <div className="text-sm">Gebetsabend</div>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-6">Kontakt</h3>
            <ul className="space-y-4 text-gray-400">
              <li>
                <div className="text-sm font-semibold text-white mb-1">Adresse</div>
                <div className="text-sm">{mockContactInfo.address}</div>
              </li>
              <li>
                <div className="text-sm font-semibold text-white mb-1">Telefon</div>
                <a href={`tel:${mockContactInfo.phone}`} className="text-sm hover:text-white transition-colors">
                  {mockContactInfo.phone}
                </a>
              </li>
              <li>
                <div className="text-sm font-semibold text-white mb-1">E-Mail</div>
                <a href={`mailto:${mockContactInfo.email}`} className="text-sm hover:text-white transition-colors">
                  {mockContactInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Social Media & Bottom Bar */}
        <div className="pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Social Media */}
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-sm">Folgen Sie uns:</span>
              <div className="flex gap-3">
                <a
                  href={mockContactInfo.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center 
                           hover:bg-blue-600 transition-all hover:scale-110"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={mockContactInfo.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center 
                           hover:bg-pink-600 transition-all hover:scale-110"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href={mockContactInfo.socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center 
                           hover:bg-red-600 transition-all hover:scale-110"
                  aria-label="YouTube"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm">
                © {currentYear} Internationale Christengemeinde Hannover
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Alle Rechte vorbehalten
              </p>
              <a
                href="/admin/login"
                className="text-gray-600 hover:text-gray-400 text-xs mt-2 inline-block transition-colors"
                title="Admin-Bereich"
              >
                Admin
              </a>
              <span className="text-gray-700 mx-2">|</span>
              <a
                href="/datenschutz"
                className="text-gray-600 hover:text-gray-400 text-xs mt-2 inline-block transition-colors"
                data-testid="footer-datenschutz-link"
              >
                Datenschutzerklärung
              </a>
              <span className="text-gray-700 mx-2">|</span>
              <a
                href="/impressum"
                className="text-gray-600 hover:text-gray-400 text-xs mt-2 inline-block transition-colors"
                data-testid="footer-impressum-link"
              >
                Impressum
              </a>
              <span className="text-gray-700 mx-2">|</span>
              <a
                href="/agb"
                className="text-gray-600 hover:text-gray-400 text-xs mt-2 inline-block transition-colors"
                data-testid="footer-agb-link"
              >
                AGB
              </a>
            </div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-12 bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm 
                        rounded-2xl p-8 border border-white/10">
          <div className="text-center max-w-2xl mx-auto">
            <Mail className="w-12 h-12 mx-auto mb-4 text-blue-300" />
            <h3 className="text-2xl font-bold mb-2">Bleiben Sie informiert</h3>
            <p className="text-gray-400 mb-6">
              Melden Sie sich für unseren Newsletter an und erhalten Sie Updates über 
              Veranstaltungen, Predigten und mehr.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Ihre E-Mail-Adresse"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                data-testid="footer-newsletter-email"
                className="flex-1 px-6 py-3 rounded-lg bg-white/10 border border-white/20 
                         text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                         focus:ring-blue-500"
              />
              <button 
                type="submit"
                disabled={newsletterLoading}
                data-testid="footer-newsletter-submit"
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold 
                               transition-all hover:scale-105 whitespace-nowrap
                               disabled:opacity-50 disabled:cursor-not-allowed">
                {newsletterLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Anmelden'}
              </button>
            </form>
            {newsletterMsg && (
              <div data-testid="footer-newsletter-message" className={`mt-4 text-sm flex items-center justify-center gap-2 ${newsletterMsg.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>
                {newsletterMsg.type === 'success' && <CheckCircle className="w-4 h-4" />}
                {newsletterMsg.text}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
